// @flow

import React, {useContext, useEffect, useRef, useState} from 'react';

import {withErrorBoundary} from '../ErrorBoundary/ErrorBoundary';
import {VaEdapEvents} from '../../utils/EDAP/VA';
import SystemContext from '../context/SystemContext';

/* molecule */
import {VaFloating} from '../molecules/VaFloating/VaFloating';

type AnalyticsData = {
    sessionid: string,
    visitorid: string,
};

type Props = {
    analyticsData: AnalyticsData,
    autoOpen: boolean,
    chatWidgetCounter: number,
    eventsInfo: any,
    getHelpText: () => string,
    intent: string,
    isLoading: boolean,
    onDismissHelpText: (any) => void,
    otherIntentProps: any,
    updateIsLoading: (boolean) => void,
};

const {
    onChatFrameOpenSuccess,
    onDismissHelpText: onDismissHelpTextEdap,
    onError,
    onVaInitDone,
    onVaOpenClick,
} = VaEdapEvents;

const VaWrapInternal = ({
    analyticsData,
    autoOpen,
    chatWidgetCounter,
    eventsInfo,
    getHelpText,
    intent,
    isLoading,
    onDismissHelpText,
    otherIntentProps,
    updateIsLoading,
}: Props) => {
    const [vaInfo, setVaInfo] = useState();
    const [helpText, setHelpText] = useState();
    const {
        system: {isDesktop, isMobile, locale},
    } = useContext(SystemContext);
    const {sessionid: sessionId, visitorid: visitorId} = analyticsData;
    const propsToPass = {
        isMobile: `${isMobile}`,
        skipWelcome: 'true',
        posLanguage: locale,
        ...eventsInfo,
    };
    if (sessionId) {
        propsToPass.sessionId = sessionId;
    }
    if (visitorId) {
        propsToPass.visitorId = visitorId;
    }
    const conversationProps = useRef(propsToPass).current;

    useEffect(() => {
        const _vaInfo = window.vaInfo;
        if (
            _vaInfo?.config?.channelOriginId &&
            _vaInfo?.config?.partnerId &&
            _vaInfo?.config?.isEnabled === 'true'
        ) {
            const config = _vaInfo.config;
            window.__VAC_ENVIRONMENT__ = config.vaEnv;
            setVaInfo(_vaInfo);
            if (getHelpText) {
                setHelpText(getHelpText());
            }
        }
    }, []);
    const onDismissHelpTextInternal = () => {
        onDismissHelpTextEdap(eventsInfo);
        onDismissHelpText();
    };
    const chatbotAuthInput = vaInfo
        ? {
              channelOriginId: vaInfo.config.channelOriginId,
              chatbotApplicationType: 'WEB',
              cpPartnerId: vaInfo.config.partnerId,
              participant: {
                  authState: 'AUTHENTICATED',
                  participantType: 'CUSTOMER',
              },
          }
        : null;

    return vaInfo ? (
        <div data-testid="va-floating-wrap">
            <VaFloating
                autoOpen={autoOpen}
                chatbotAuthInput={chatbotAuthInput}
                chatWidgetCounter={chatWidgetCounter}
                conversationProperties={conversationProps}
                eventsInfo={eventsInfo}
                helpText={helpText}
                intent={intent}
                isDesktop={isDesktop}
                isLoading={isLoading}
                onChatFrameOpenSuccess={onChatFrameOpenSuccess}
                onDismissHelpText={onDismissHelpTextInternal}
                onError={onError}
                otherIntentProps={otherIntentProps}
                onVaInitDone={onVaInitDone}
                onVaOpenClick={onVaOpenClick}
                updateIsLoading={updateIsLoading}
                vaInfo={vaInfo}
            />
        </div>
    ) : null;
};

const VaWrap = (propsToPass: Props) => {
    const [analyticsData, setAnalyticsData] = useState();

    useEffect(() => {
        setAnalyticsData(window.analyticsdatalayer || {});
    }, []);

    return analyticsData
        ? withErrorBoundary(
              <VaWrapInternal {...propsToPass} analyticsData={analyticsData} />,
              'va-wrap'
          )
        : null;
};

export default VaWrap;
